import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight, Truck, AlertTriangle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal, clearCart, validateCart, stockIssues } = useCart();
  const empty = items.length === 0;
  useEffect(() => { validateCart(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cart-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-brand-navy mb-6">Your Cart</h1>

      {empty ? (
        <div className="text-center bg-white border border-slate-200 rounded-2xl py-20">
          <ShoppingBag className="h-12 w-12 mx-auto text-slate-300" />
          <div className="font-heading text-2xl text-brand-navy mt-4">Cart is empty</div>
          <p className="text-slate-500 mt-1">Warehouse is stocked. Grab a deal.</p>
          <Link to="/shop" data-testid="cart-empty-shop-btn" className="mt-5 inline-flex items-center gap-2 bg-brand-navy text-white rounded-full px-6 h-11 font-bold">
            Start shopping <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`cart-item-${it.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4">
                <Link to={`/product/${it.slug}`} className="shrink-0">
                  <img src={it.featured_image} alt="" className="h-24 w-24 rounded-lg object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.slug}`} className="font-bold text-slate-900 hover:text-brand-red line-clamp-2">{it.product_name}</Link>
                  <div className="text-xs text-slate-500 mt-0.5">{it.brand} · {it.condition}</div>
                  {stockIssues[it.id] && <div className="text-xs font-bold text-brand-red mt-1 inline-flex items-center gap-1" data-testid={`cart-stock-issue-${it.id}`}><AlertTriangle className="h-3 w-3" /> {stockIssues[it.id]}</div>}
                  <div className="mt-2 flex items-center gap-3">
                    <div className="inline-flex items-center border border-slate-200 rounded-full h-9">
                      <button data-testid={`cart-decrement-${it.id}`} onClick={() => updateQuantity(it.id, it.quantity - 1)} className="w-8 font-bold">−</button>
                      <span className="w-8 text-center font-bold">{it.quantity}</span>
                      <button data-testid={`cart-increment-${it.id}`} disabled={it.quantity >= (it.max_available || 1)} onClick={() => updateQuantity(it.id, it.quantity + 1)} className="w-8 font-bold disabled:opacity-30">+</button>
                    </div>
                    <button data-testid={`cart-remove-${it.id}`} onClick={() => removeItem(it.id)} className="text-slate-500 hover:text-brand-red text-sm inline-flex items-center gap-1">
                      <Trash2 className="h-4 w-4" /> Remove
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-brand-navy text-lg">{money(it.price * it.quantity)}</div>
                  {it.was && it.was > it.price && (
                    <div className="text-xs text-slate-400 line-through">{money(it.was * it.quantity)}</div>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2">
              <Link to="/shop" className="text-sm font-bold text-brand-navy hover:text-brand-red">← Continue shopping</Link>
              <button onClick={clearCart} data-testid="cart-clear-btn" className="text-sm text-slate-500 hover:text-brand-red">Clear cart</button>
            </div>
          </div>

          <aside className="bg-white border border-slate-200 rounded-2xl p-6 h-fit sticky top-24 space-y-4">
            <div className="font-heading text-xl text-brand-navy">Order Summary</div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-bold">{money(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Taxes</span><span className="text-slate-400">Calculated at checkout</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span className="text-slate-400">—</span></div>
            <div className="pt-3 border-t flex justify-between items-baseline">
              <span className="font-heading text-brand-navy">Total</span>
              <span className="font-heading text-xl text-brand-navy">{money(subtotal)}</span>
            </div>
            <Link to="/checkout" data-testid="cart-checkout-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white font-black uppercase tracking-wider h-11 rounded-full active:scale-95 transition">
              Proceed to Checkout <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="rounded-xl bg-brand-greenLight border border-brand-green/30 p-3 flex items-start gap-2 text-brand-greenDark text-xs">
              <Truck className="h-4 w-4 mt-0.5" />
              <div>Pay online now, then drive to our Barrhaven pickup lane to collect your order.</div>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
